import { ReactElement, useEffect, useRef, useState } from 'react'
import { GetSurveyResponse, SurveyQuestion, SurveyAnswerMap, SurveyQuestionChoice } from '../../models/global.types'
import { SurveyQuestionType } from '../../enums/enums'
import apis from '../../libs/services'
import { Button, LoadingSpinner, Text } from '../atoms'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'
import HexChoiceButton from '../atoms/HexChoiceButton/HexChoiceButton'
import AnonymityCertification from './AnonymityCertification'
import AnonymityTags from './AnonymityTags'

type SurveyStep = 'certification' | 'tags' | 'survey'

interface SurveyProps {
  surveyId: string
  onFinish?: () => void
}

const kbControlsLayer = 'survey'
const surveyButtonId = 'survey-button-next'
const surveyPageClass = 'bg-dopage-black min-h-screen flex flex-col items-center justify-center font-primary px-4 md:px-0'
const surveyCardClass = 'bg-dopage-charcoalGray p-6 md:p-8 w-full max-w-[600px] h-[85dvh] md:h-[750px] flex flex-col items-center justify-center overflow-y-auto md:mx-4'

const getQuestionInputId = (questionId: string): string => `survey-input-${questionId}`
const getQuestionLabelId = (questionId: string): string => `survey-label-${questionId}`

// Max answer choices rendered for single/multi-select questions (DMVP-1051: raised 12 -> 20)
const MAX_SELECT_CHOICES = 20

// Hide unused/blank slots, cap at MAX_SELECT_CHOICES, and split into two columns.
// For <=12 choices, keep the historical layout exactly (up to 6 in column1, the rest in column2:
// 1-6 render as a single column, 7 as 6/1, 12 as 6/6). Beyond 12, balance the two columns
// (13 -> 7/6, 14 -> 7/7, 20 -> 10/10).
export const splitChoicesIntoColumns = (choices: SurveyQuestionChoice[]): { column1: SurveyQuestionChoice[], column2: SurveyQuestionChoice[] } => {
  const visibleChoices = choices
    .filter(choice => choice.text != null && choice.text.trim() !== '')
    .slice(0, MAX_SELECT_CHOICES)
  const splitIndex = visibleChoices.length <= 12 ? 6 : Math.ceil(visibleChoices.length / 2)
  return {
    column1: visibleChoices.slice(0, splitIndex),
    column2: visibleChoices.slice(splitIndex)
  }
}

function Survey ({ surveyId, onFinish }: SurveyProps): ReactElement {
  const keyboardControls = useKeyboardControls()
  const [step, setStep] = useState<SurveyStep>('certification')
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null)
  const [surveyData, setSurveyData] = useState<GetSurveyResponse | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, SurveyAnswerMap>>({})
  const [shouldFocusInput, setShouldFocusInput] = useState(true)
  const textAnswerDebounceRef = useRef<NodeJS.Timeout | null>(null)

  // Push layer for keyboard controls
  useEffect(() => {
    keyboardControls.pushActiveLayer(kbControlsLayer)
    return () => keyboardControls.resetDefaultLayer()
  }, [keyboardControls])

  const handleCertificationContinue = (): void => {
    setStep('tags')
  }

  const handleTagsSubmit = async (anonTag: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const startRes = await apis.startSurvey(surveyId, anonTag)
      const responseId = startRes.data?.surveyResponseId
      if (typeof responseId !== 'string' || responseId === '') {
        throw new Error('Failed to start survey')
      }
      setSurveyResponseId(responseId)

      const surveyRes = await apis.getSurveyWithProgress(surveyId, responseId)
      setSurveyData(surveyRes.data)
      setStep('survey')
    } catch (err: any) {
      const errorMessage = (err instanceof Error) ? err.message : 'Failed to start survey'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Set focus on question input when question changes
  useEffect(() => {
    if (!shouldFocusInput || surveyData === null || surveyData.questions.length === 0) return
    const inputId = getQuestionInputId(surveyData.questions[currentIndex].id)
    keyboardControls.setFocusedElement(inputId, kbControlsLayer)
    setShouldFocusInput(false)
  }, [shouldFocusInput, surveyData, currentIndex, keyboardControls])

  const updateAnswer = (questionId: string, answer: SurveyAnswerMap): void => {
    const newAnswers = { ...answers, [questionId]: answer }
    setAnswers(newAnswers)
  }

  // Render certification step
  if (step === 'certification') {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <AnonymityCertification onContinue={handleCertificationContinue} />
        </div>
      </div>
    )
  }

  // Render tags step
  if (step === 'tags') {
    if (loading) {
      return (
        <div className={surveyPageClass}>
          <div className={surveyCardClass}>
            <LoadingSpinner className='w-32 md:w-48' />
          </div>
        </div>
      )
    }

    if (error !== null) {
      return (
        <div className={surveyPageClass}>
          <div className={surveyCardClass}>
            <Text text={error} size='LG' className='text-red-500 mb-4' />
            <AnonymityTags onSubmit={handleTagsSubmit} />
          </div>
        </div>
      )
    }

    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <AnonymityTags onSubmit={handleTagsSubmit} />
        </div>
      </div>
    )
  }

  // Survey step - show loading if still loading
  if (loading) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <LoadingSpinner className='w-32 md:w-48' />
        </div>
      </div>
    )
  }

  // Survey step - show error if any
  if (error !== null) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text={error} size='LG' className='text-red-500' />
        </div>
      </div>
    )
  }

  // Survey step - no data
  if (surveyData === null || surveyData.questions.length === 0) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text='No survey found' size='LG' className='text-dopage-lightGreen' />
        </div>
      </div>
    )
  }

  const currentQuestion = surveyData.questions[currentIndex]
  const isLastQuestion = currentIndex === surveyData.questions.length - 1
  const progressPercent = ((currentIndex + 1) / surveyData.questions.length) * 100

  const hasValidAnswerForQuestion = (questionId: string): boolean => {
    const answer = answers[questionId]
    if (answer == null) return false
    const textLength = answer.textResponse?.trim().length ?? 0
    const choiceCount = answer.choiceIds?.length ?? 0
    return textLength > 0 || choiceCount > 0
  }
  const isNextDisabled = currentQuestion.isRequired && !hasValidAnswerForQuestion(currentQuestion.id)

  const handleNext = (): void => {
    if (isLastQuestion || surveyResponseId === null) return
    const answer = answers[currentQuestion.id]
    if (answer !== undefined) {
      // Fire and forget - don't block UI
      // Build request - only include textResponse if it has a value (for text questions)
      const request: { textResponse?: string, choiceIds?: string[] } = {
        choiceIds: answer.choiceIds
      }
      const textResponse = answer.textResponse?.trim()
      if (textResponse !== undefined && textResponse !== '') {
        request.textResponse = textResponse
      }
      void apis.submitSurveyAnswer(surveyId, surveyResponseId, currentQuestion.id, request)
    }
    setCurrentIndex(prev => prev + 1)
    setShouldFocusInput(true)
  }

  const handleFinish = async (): Promise<void> => {
    if (surveyResponseId === null) return
    const answer = answers[currentQuestion.id]
    if (answer !== undefined) {
      // Build request - only include textResponse if it has a value (for text questions)
      const request: { textResponse?: string, choiceIds?: string[] } = {
        choiceIds: answer.choiceIds
      }
      const textResponse = answer.textResponse?.trim()
      if (textResponse !== undefined && textResponse !== '') {
        request.textResponse = textResponse
      }
      await apis.submitSurveyAnswer(surveyId, surveyResponseId, currentQuestion.id, request)
    }
    await apis.completeSurvey(surveyId, surveyResponseId)

    if (onFinish != null) {
      onFinish()
    }
  }

  const renderQuestionInputSectionText = (question: SurveyQuestion): ReactElement => {
    const currentAnswer = answers[question.id]?.textResponse ?? ''
    const inputId = getQuestionInputId(question.id)
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value
      if (textAnswerDebounceRef.current !== null) clearTimeout(textAnswerDebounceRef.current)
      textAnswerDebounceRef.current = setTimeout(() => {
        updateAnswer(question.id, { textResponse: value, choiceIds: [] })
      }, 300)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Tab') {
        e.preventDefault()
        if (e.shiftKey) {
          keyboardControls.focusPreviousElement()
        } else {
          keyboardControls.focusNextElement()
        }
      }
    }

    return (
      <>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(inputId)}
          highlightEffect={HighlightEffect.InputOutline}
        >
          <KeyboardControl layer={kbControlsLayer} manualFocusHandling>
            <textarea
              key={question.id}
              id={inputId}
              aria-required={question.isRequired}
              aria-invalid={isInvalidRequired}
              defaultValue={currentAnswer}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder='Type your answer here...'
              className='w-full h-32 md:h-40 p-4 bg-dopage-black text-dopage-lightGreen border border-dopage-tealGray rounded-lg resize-none focus:outline-none focus:border-dopage-primary'
            />
          </KeyboardControl>
        </ControlHighlight>
      </>
    )
  }

  const renderQuestionInputSectionSingleSelect = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <div className='text-dopage-disabled'>No choices available</div>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds?.[0] ?? ''
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const { column1, column2 } = splitChoicesIntoColumns(question.choices)

    const handleSelectChange = (choiceId: string): void => {
      updateAnswer(question.id, { choiceIds: [choiceId] })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isSelected = currentAnswer === choice.id
      return (
        <HexChoiceButton
          key={choice.id}
          id={inputId}
          label={choice.text}
          isSelected={isSelected}
          isRequired={question.isRequired}
          isInvalid={isInvalidRequired}
          type='radio'
          onClick={() => handleSelectChange(choice.id)}
          layer={kbControlsLayer}
        >
          <span aria-hidden='true' className='text-dopage-lightGreen text-base md:text-lg'>{choice.text}</span>
        </HexChoiceButton>
      )
    }

    return (
      <>
        <div role='radiogroup' aria-labelledby={getQuestionLabelId(question.id)} className='flex flex-col md:flex-row gap-4 md:gap-6'>
          <div className='flex flex-col gap-4 md:gap-5 flex-1'>
            {column1.map(renderChoice)}
          </div>
          {column2.length > 0 && (
            <div className='flex flex-col gap-4 md:gap-5 flex-1'>
              {column2.map(renderChoice)}
            </div>
          )}
        </div>
      </>
    )
  }

  const renderQuestionInputSectionMultiSelect = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <div className='text-dopage-disabled'>No choices available</div>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds ?? []
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const { column1, column2 } = splitChoicesIntoColumns(question.choices)

    const handleToggleChoice = (choiceId: string): void => {
      const newChoices = currentAnswer.includes(choiceId)
        ? currentAnswer.filter(id => id !== choiceId)
        : [...currentAnswer, choiceId]
      updateAnswer(question.id, { choiceIds: newChoices })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isChecked = currentAnswer.includes(choice.id)
      return (
        <HexChoiceButton
          key={choice.id}
          id={inputId}
          label={choice.text}
          isSelected={isChecked}
          isRequired={question.isRequired}
          isInvalid={isInvalidRequired}
          type='checkbox'
          onClick={() => handleToggleChoice(choice.id)}
          layer={kbControlsLayer}
        >
          <span aria-hidden='true' className='text-dopage-lightGreen text-base md:text-lg'>{choice.text}</span>
        </HexChoiceButton>
      )
    }

    return (
      <>
        <div role='group' aria-labelledby={getQuestionLabelId(question.id)} className='flex flex-col md:flex-row gap-4 md:gap-6'>
          <div className='flex flex-col gap-4 md:gap-5 flex-1'>
            {column1.map(renderChoice)}
          </div>
          {column2.length > 0 && (
            <div className='flex flex-col gap-4 md:gap-5 flex-1'>
              {column2.map(renderChoice)}
            </div>
          )}
        </div>
      </>
    )
  }

  const renderQuestionInputSectionLikert = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <div className='text-dopage-disabled'>No choices available</div>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds?.[0] ?? ''
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const choices = question.choices.slice(0, 5) // 5-point Likert scale

    const handleSelectChange = (choiceId: string): void => {
      updateAnswer(question.id, { choiceIds: [choiceId] })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isSelected = currentAnswer === choice.id

      return (
        <div key={choice.id} className='flex flex-row md:flex-col items-center gap-3 md:gap-1 relative z-10 md:min-w-[80px]'>
          <HexChoiceButton
            id={inputId}
            label={choice.text}
            isSelected={isSelected}
            isRequired={question.isRequired}
            isInvalid={isInvalidRequired}
            type='radio'
            onClick={() => handleSelectChange(choice.id)}
            layer={kbControlsLayer}
          />
          <span aria-hidden='true' className='text-dopage-lightGreen text-sm md:text-xs md:mt-1 text-left md:text-center leading-tight uppercase md:max-w-[80px] break-words'>
            {choice.text}
          </span>
        </div>
      )
    }

    return (
      <div
        role='radiogroup'
        aria-labelledby={getQuestionLabelId(question.id)}
        className='flex flex-col md:flex-row md:justify-between gap-4 py-6 px-2 relative'
      >
        {/* Connector line: vertical (left) on mobile, horizontal on desktop */}
        <div className='md:hidden absolute top-8 bottom-8 left-5 w-0.5 -translate-x-1/2 bg-dopage-tealGray opacity-100' />
        <div className='hidden md:block absolute top-1/2 left-12 right-12 h-0.5 -translate-y-[20px] bg-dopage-tealGray opacity-100' />

        {choices.map(renderChoice)}
      </div>
    )
  }

  const renderQuestionInputSection = (question: SurveyQuestion): ReactElement => {
    switch (question.questionType) {
      case SurveyQuestionType.Text:
        return renderQuestionInputSectionText(question)
      case SurveyQuestionType.SingleSelect:
        return renderQuestionInputSectionSingleSelect(question)
      case SurveyQuestionType.MultiSelect:
        return renderQuestionInputSectionMultiSelect(question)
      case SurveyQuestionType.Likert:
        return renderQuestionInputSectionLikert(question)
      default:
        return <div className='text-dopage-disabled'>[Unknown question type]</div>
    }
  }

  return (
    <div className={surveyPageClass}>
      <div className='bg-dopage-charcoalGray w-full max-w-[600px] h-[85dvh] md:h-[750px] flex flex-col relative overflow-hidden md:mx-4'>
        <div className='p-6 md:p-8 flex-1 flex flex-col overflow-y-auto'>
          <Text
            text={currentQuestion.text}
            size='LG'
            id={getQuestionLabelId(currentQuestion.id)}
            className='text-dopage-lightGreen mb-6 md:mb-8 text-lg md:text-xl'
          />

          <div className='flex-1'>
            {renderQuestionInputSection(currentQuestion)}
          </div>

          <div className='flex justify-center mt-6 md:mt-0'>
            <ControlHighlight
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(surveyButtonId)}
              highlightEffect={HighlightEffect.ButtonHexOutline}
            >
              <KeyboardControl layer={kbControlsLayer}>
                <Button
                  id={surveyButtonId}
                  disabled={isNextDisabled}
                  onClick={isLastQuestion ? handleFinish : handleNext}
                  className='px-8 py-3 font-semibold'
                >
                  <span>{isLastQuestion ? 'Finish' : 'Next'}</span>
                </Button>
              </KeyboardControl>
            </ControlHighlight>
          </div>
        </div>

        <div className='h-3 md:h-4 bg-dopage-tealGray w-full'>
          <div
            role='progressbar'
            aria-valuemin={1}
            aria-valuemax={surveyData.questions.length}
            aria-valuenow={currentIndex + 1}
            aria-valuetext={`Question ${currentIndex + 1} of ${surveyData.questions.length}`}
            aria-label='Survey progress'
            className='h-full bg-dopage-primary transition-all duration-300'
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default Survey
